<template>
  <m-card-item
    class="data-source-item"
    :icon="icon"
    :clickable="clickable"
    @click="$emit('click')"
  >
    <div class="_left">
      <span>{{ dataSourceName(dataSource) }}</span>
    </div>
    <template #right>
      <div class="_right">
        <m-tooltip
          v-if="isIncomplete"
          placement="top"
        >
          <template #title>
            {{ $t('dataSourceItem.incompleteDescription') }}
          </template>
          <m-btn
            icon="warning"
            hide-border
            xs
            :icon-color="$colors.yellow.base"
            :disabled="disableManageActions"
            @click.stop="edit"
          >
            {{ $t('dataSourceItem.establishAction') }}
          </m-btn>
        </m-tooltip>
        <template v-if="expired">
          <m-tooltip
            v-if="!expireWarningAction"
            placement="top"
          >
            <template #title>
              {{ dataSource.dead ? $t('dataSourceItem.connectionExpiredDescription') : $t('dataSourceItem.connectionUnhealthyDescription') }}
            </template>
            <div :style="{display: 'flex', gap: '.4rem'}">
              <m-icon
                type="warning"
                :color="dataSource.dead ? $colors.red.base : $colors.yellow.base"
              />
              {{ dataSource.dead ? $t('dataSourceItem.connectionExpired') : $t('dataSourceItem.connectionUnhealthy') }}
            </div>
          </m-tooltip>
          <m-tooltip
            v-else
            placement="top"
          >
            <template #title>
              {{ $t('dataSourceItem.refreshDescription') }}
            </template>
            <div>
              <m-btn
                icon="warning"
                hide-border
                xs
                :icon-color="dataSource.dead ? $colors.red.base : $colors.yellow.base"
                :disabled="disableManageActions"
                @click.stop="refresh"
              >
                {{ $t('dataSourceItem.refreshAction') }}
              </m-btn>
            </div>
          </m-tooltip>
        </template>

        <m-tooltip>
          <template #title>
            {{ $t('dataSourceItem.creatorDescription', { creatorName }) }}
          </template>
          <user-display
            :user="creator"
            hide-name
            xs
          />
        </m-tooltip>
        <m-dropdown
          v-model:value="showActions"
          :title="$t('general.actions')"
          close-on-click
          :disabled="disableManageActions"
        >
          <m-btn
            icon="ellipsis"
            fab
            xs
            hide-border
            :disabled="disableManageActions"
            @click.stop="showActions = true"
          />
          <template #overlay>
            <m-card
              no-padding
              list
            >
              <m-card-item @click="edit">
                {{ $t('general.edit') }}
              </m-card-item>
              <m-card-item @click="deleteItem">
                {{ $t('general.delete') }}
              </m-card-item>
            </m-card>
          </template>
        </m-dropdown>
      </div>
    </template>
  </m-card-item>
  <m-dialog
    v-model:value="showEdit"
    hide-footer
    :title="$t('dataSourceItem.editTitle', {source: $t(`dataSources.${camelCase(dataSource.type)}`)})"
  >
    <jira-editor
      v-if="dataSource.type === dataSourceType.jira"
      :entity="dataSource"
      @close="close"
    />
    <data-source-editor
      v-else
      :entity="dataSource"
      @close="close"
    />
  </m-dialog>
</template>

<script>
import DataSourceEditor from '@/components/datasource/DataSourceEditor.vue';
import JiraEditor from '@/components/datasource/JiraEditor.vue';
import UserDisplay from 'shared/components/UserDisplay.vue';
import useAccess from '@/composables/access/access';
import useDataSources from '@/composables/integrations/datasources';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useUser from 'shared/composables/user';
import useUsers from '@/composables/user/users';
import { accessGroupFlag, dataSourceAuthenticationType, dataSourceType } from 'shared/constants.json';
import { camelCase } from 'lodash-es';
import { computed } from 'vue';
import { connectUrl, dataSourceIcon } from '@/lib/data-source';
import { dataSourceInstallStatus } from '@/constants.json';
import { initOAuthInstall } from '@/lib/oauth-redirect';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';

export default {
  name: 'DataSourceItem',
  props: {
    dataSource: {
      type: Object,
      required: true,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
    showExpireAction: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'click'],
  components: { DataSourceEditor, JiraEditor, UserDisplay },
  setup(props) {
    const i18n = useI18n();
    const { userHasRight } = useAccess();

    const { loggedInUser } = useLoggedInUser();
    const { dataSourceName, fetchDataSource, deleteDataSource } = useDataSources();
    const { goalSettings } = useGoalSettings();

    const userSvc = useUsers();

    const creator = computed(() => {
      if (props.dataSource.creator === null) {
        return null;
      }
      return userSvc.selectSingle(props.dataSource.creator.uid);
    });
    const creatorName = computed(() => {
      if (creator.value === null) {
        return i18n.t('user.deleted.name');
      }
      return useUser(creator).displayName.value;
    });
    return {
      loggedInUser,
      goalSettings,

      dataSourceName,
      fetchDataSource,
      deleteDataSource,

      userHasRight,
      accessGroupFlag,

      creator,
      creatorName,
    };
  },
  data() {
    return { showActions: false, showEdit: false, camelCase, dataSourceType };
  },
  computed: {
    icon() {
      return dataSourceIcon(this.dataSource);
    },
    expired() {
      return !this.dataSource.healthy || this.dataSource.dead;
    },
    expireWarningAction() {
      if (!this.expired) {
        return false;
      }
      if (this.dataSource.creator !== null && this.dataSource.creator.uid !== this.loggedInUser.uid) {
        return false;
      }
      return this.showExpireAction;
    },
    isIncomplete() {
      switch (this.dataSource.authenticationType) {
        case dataSourceAuthenticationType.oauth:
        case dataSourceAuthenticationType.oauthv2:
          return this.dataSource.accessToken === '';
        case dataSourceAuthenticationType.apiToken:
          return this.dataSource.apiToken === '' || this.dataSource.apiServer === '' || this.dataSource.apiUser === '';
        default:
          return false;
      }
    },
    disableManageActions() {
      if (this.dataSource.creator === null && this.dataSource.shared) {
        return false;
      }

      if (this.dataSource.creator.uid === this.loggedInUser.uid) {
        return false;
      }

      return !this.userHasRight([accessGroupFlag.accountWriteAccess]);
    },
  },
  methods: {
    edit() {
      this.showEdit = true;
    },
    refresh() {
      initOAuthInstall(connectUrl(this.dataSource)).then((event) => {
        if (event.data.status !== dataSourceInstallStatus.success) {
          return;
        }
        this.$showSnackbar({ color: 'success', message: this.$t('dataSource.successfullySynced') });
      });
    },
    close() {
      this.showEdit = false;
      this.$emit('close');
    },
    deleteItem() {
      const deleteMethod = () => this.deleteDataSource(this.dataSource).then(() => {
        this.$showSnackbar({ color: 'success', message: this.$t('success.deleted') });
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      }));

      this.$confirm({
        title: this.$t('dataSourceItem.deletePrompt', { title: this.goalSettings.featureNamePlural }),
        okText: this.$t('general.yesDelete'),
        okType: 'danger',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk() {
          deleteMethod();
        },
      });
    },
  },
};
</script>

<style lang="scss" type="text/scss" scoped>
  .data-source-item {
    ._left {
      display:  flex;
      gap: .8rem;
      align-items: center;
    }

    ._right {
      display:  flex;
      gap: .8rem;
      align-items: center;

      ._btn {
        display:  flex;
        gap: .4rem;
      }
    }

    .user-display {
       color: $font-color-secondary;
    }
  }
</style>
